/**
 * Доход: ритм, ожидания и горизонт.
 *
 * До этого модуля продукт знал про доход одно число месяца и флаг «сумма
 * плавает», и этого хватало ровно для одного человека — с окладом. Сдельщику
 * приложение предлагало назвать число, которого у него нет; он называл наугад,
 * и дальше от выдумки считались главная цифра, цифра в день, деление платежей
 * на «сейчас» и «из следующей зарплаты» и цикл взносов в цели.
 *
 * Здесь свойства дохода разведены, потому что в жизни они независимы:
 *
 * · **ритм** — по числам, каждую неделю, раз в две недели, когда как;
 * · **дата** — посчитана из ритма, названа человеком или оценена по истории;
 * · **сумма** — известна, оценена или неизвестна;
 * · **роль** — повторяемый источник или разовые деньги.
 *
 * Главное правило модуля: **горизонт не выдумывается**. Нет подтверждённой
 * даты — нет и горизонта, и интерфейс переходит на язык запаса хода. Молча
 * подставленное первое число следующего месяца или «плюс 30 дней» опаснее
 * отсутствия ответа: человек тратит по обещанию, которого никто не давал.
 */
import { ZERO, clampPos, share, sub, type Money } from './money.js';
import {
  addDays, dayWords, diffDays, isAfter, isBefore, nextOccurrence, nextOccurrenceInclusive,
  prevOccurrenceInclusive, type PlainDate,
} from './date.js';
import type {
  HorizonRole, IncomeAmountBasis, IncomeExpectation, IncomeRhythm, IncomeStream,
} from './types.js';

// ───────────────────────────────────────────────────────────────── ритм

/**
 * Ритм источника. Старая запись без `rhythm` читается как «по числу месяца».
 *
 * Именно здесь живёт совместимость: миграция данных не нужна, потому что
 * прежняя пара `dayOfMonth`/`shiftRule` однозначно описывает ежемесячный ритм.
 */
export function rhythmOf(s: IncomeStream): IncomeRhythm {
  if (s.rhythm) return s.rhythm;
  return { kind: 'dates', days: [s.dayOfMonth], shiftRule: s.shiftRule };
}

/** Ритм вообще называет даты? У «когда как» и «не жду» их нет. */
export const hasDates = (r: IncomeRhythm): boolean =>
  r.kind === 'dates' || r.kind === 'weekly' || r.kind === 'biweekly';

/**
 * Ближайшее наступление по ритму, строго после `today`.
 *
 * `null` — ритм дат не называет. Это не ошибка и не повод подставить месяц:
 * ответ «не знаю» здесь такой же законный, как дата.
 */
export function nextByRhythm(r: IncomeRhythm, today: PlainDate): PlainDate | null {
  if (r.kind === 'dates') {
    const dates = r.days
      .filter((d) => Number.isInteger(d) && d >= 1 && d <= 31)
      .map((d) => nextOccurrence(today, d, r.shiftRule));
    if (dates.length === 0) return null;
    return dates.reduce((a, b) => (a <= b ? a : b));
  }
  if (r.kind === 'weekly' || r.kind === 'biweekly') {
    const step = r.kind === 'weekly' ? 7 : 14;
    // Шаг от опорной даты, а не от сегодня: у двухнедельного дохода важна
    // чётность недели, и «через 14 дней от сегодня» её теряет.
    const passed = diffDays(r.anchor, today);
    const steps = Math.floor(passed / step) + 1;
    return addDays(r.anchor, steps * step);
  }
  return null;
}

/** Ближайшее наступление источника **после** сегодня. `null` — дат нет. */
export const nextForStream = (s: IncomeStream, today: PlainDate): PlainDate | null =>
  (s.isActive ? nextByRhythm(rhythmOf(s), today) : null);

/**
 * Все наступления источника в окне `[from, to]` включительно.
 *
 * Нужны везде, где доход участвует в прогнозе по дням: недельный приход даёт
 * четыре поступления в месяц, а не одно, и считать его ежемесячным значит
 * ошибиться вчетверо.
 */
export function incomeOccurrences(
  s: IncomeStream, from: PlainDate, to: PlainDate,
): PlainDate[] {
  if (!s.isActive) return [];
  const rhythm = rhythmOf(s);
  if (!hasDates(rhythm)) return [];

  const out: PlainDate[] = [];
  // Идём от дня перед началом окна: `nextByRhythm` строго больше аргумента.
  let cursor = addDays(from, -1);
  // Предел витков — длина окна: наступление не бывает чаще раза в день.
  const limit = Math.max(1, diffDays(from, to) + 1);
  for (let i = 0; i < limit; i += 1) {
    const next = nextByRhythm(rhythm, cursor);
    if (!next || isAfter(next, to)) break;
    out.push(next);
    cursor = next;
  }
  return out;
}

/** Предыдущее наступление по расписанию, включая сегодняшнее. */
export function previousOccurrence(s: IncomeStream, today: PlainDate): PlainDate | null {
  if (!s.isActive) return null;
  const rhythm = rhythmOf(s);
  if (rhythm.kind === 'dates') {
    const days = rhythm.days.filter((d) => Number.isInteger(d) && d >= 1 && d <= 31);
    if (days.length === 0) return null;
    const dates = days.map((d) => prevOccurrenceInclusive(today, d, rhythm.shiftRule));
    return dates.reduce((a, b) => (a >= b ? a : b));
  }
  if (rhythm.kind === 'weekly' || rhythm.kind === 'biweekly') {
    const step = rhythm.kind === 'weekly' ? 7 : 14;
    const passed = diffDays(rhythm.anchor, today);
    if (passed < 0) return null;
    return addDays(rhythm.anchor, Math.floor(passed / step) * step);
  }
  return null;
}

/**
 * Факт прихода — то, что действительно случилось.
 *
 * Отдельно от ожиданий: расписание не доказывает, что деньги пришли. Пока
 * цикл целей определялся расписанием, приложение просило откладывать из
 * зарплаты, которой не было.
 */
export interface IncomeFact {
  date: PlainDate;
  streamId?: string;
  kind: 'regular' | 'oneoff' | 'refund';
}

/** Насколько раньше расписания может прийти доход и всё же закрыть его. */
const EARLY_DAYS = 3;

/**
 * Приход по этому источнику, закрывающий наступление.
 *
 * Нужен и прогнозу: зарплата, пришедшая на день раньше, уже сидит в остатке,
 * и прибавить её ещё раз в день по расписанию значит посчитать одни деньги
 * дважды.
 */
export const factClosing = (
  facts: readonly IncomeFact[], streamId: string, occurrence: PlainDate,
): IncomeFact | undefined => facts.find(
  (f) => f.kind === 'regular'
    && f.streamId === streamId
    && !isBefore(f.date, addDays(occurrence, -EARLY_DAYS)),
);

/**
 * Наступление, которого ждут прямо сейчас: сегодняшнее или просроченное.
 *
 * Утром в день зарплаты деньги ещё не пришли — и это **текущая** точка, а не
 * повод перескочить на следующий месяц. Пока приход не подтверждён, следующее
 * наступление горизонтом не становится: иначе приложение обещает дожить до
 * зарплаты, которую человек уже не получил вовремя.
 */
export function pendingOccurrence(
  s: IncomeStream, today: PlainDate, facts: readonly IncomeFact[] = [],
  since?: PlainDate,
): PlainDate | null {
  const previous = previousOccurrence(s, today);
  if (!previous) return null;
  /*
    Без начала учёта обвинять не в чем.

    «Приход не подтверждён» — упрёк, и произносить его можно только там, где
    приложение действительно следит за деньгами. Пока дата начала учёта не
    названа (старые данные, демо, расчёт по чужому снимку), прошлое
    наступление считается состоявшимся: человек назвал остатки, и приход в
    них уже сидит.
  */
  if (!since) return null;
  /*
    До начала учёта приложение за приходы не отвечает.

    Человек завёл аккаунт 18 сентября и назвал остатки на этот день — в них
    зарплата 5-го уже сидит. Считать её «ожидаемой и неподтверждённой» значит
    требовать подтверждения того, что случилось до приложения, и держать
    горизонт на дате, к которой оно не имеет отношения.
  */
  if (isBefore(previous, since)) return null;
  // Слишком старое наступление текущим не считаем: это уже не «ждём сегодня»,
  // а забытый месяц, и держать им горизонт бессмысленно.
  if (diffDays(previous, today) > 31) return null;
  return factClosing(facts, s.id, previous) ? null : previous;
}

/** Последний **фактический** приход. Расписание здесь не участвует. */
export function lastActualIncome(
  facts: readonly IncomeFact[], today: PlainDate, streamId?: string,
): PlainDate | null {
  const dates = facts
    .filter((f) => f.kind === 'regular' && (!streamId || f.streamId === streamId))
    .map((f) => f.date)
    .filter((d) => !isAfter(d, today))
    .sort();
  return dates.length > 0 ? dates[dates.length - 1]! : null;
}

/**
 * Отпечаток источника.
 *
 * По нему ответ советника и разбор настройки понимают, что картина
 * изменилась. Считался по `dayOfMonth` — и смена недельного ритма на
 * двухнедельный ответ не устаревала: подпись та же, деньги другие.
 */
export const streamFingerprint = (s: IncomeStream): string => [
  s.id,
  s.amount,
  amountBasisOf(s),
  roleOfStream(s),
  s.isActive ? 1 : 0,
  JSON.stringify(rhythmOf(s)),
].join(':');

/**
 * Задаёт ли источник срок. По умолчанию да: у большинства доход один.
 */
export const roleOfStream = (s: IncomeStream): HorizonRole => s.horizonRole ?? 'checkpoint';

/**
 * Задаёт ли ожидание срок. По умолчанию нет.
 *
 * Умолчание обратное источнику, и это не небрежность. Обещанный возврат за
 * куртку не может быть следующей точкой финансового планирования, а
 * разовых обещаний в жизни много.
 */
export const roleOfExpectation = (e: IncomeExpectation): HorizonRole => e.horizonRole ?? 'top_up';

/** Насколько точна сумма. Старая запись читается из `isVariable`. */
export const amountBasisOf = (s: IncomeStream): IncomeAmountBasis =>
  s.amountBasis ?? (s.isVariable ? 'estimate' : 'known');

/**
 * Осторожная оценка переменного дохода: при отсутствии истории — 90% от
 * названной суммы. Оптимистичная оценка дохода = сломанное обещание в конце
 * месяца, а продукт продаёт именно надёжность цифры (PRD §1.4).
 *
 * Живёт здесь, а не в «Можно потратить»: ею пользуется прогноз по дням, а
 * «Можно потратить» сам берёт из прогноза платежи по картам — держать оценку
 * там значило бы замкнуть модули друг на друга.
 */
export function conservativeIncome(s: IncomeStream): Money {
  if (!s.isVariable) return s.amount;
  return s.amountP25 ?? share(s.amount, 0.9);
}

/** Ритм словами — без жаргона и без выдуманной точности. */
export function rhythmText(r: IncomeRhythm): string {
  if (r.kind === 'weekly') return 'каждую неделю';
  if (r.kind === 'biweekly') return 'раз в две недели';
  if (r.kind === 'irregular') return 'когда как';
  if (r.kind === 'none') return 'новых поступлений не жду';
  const days = [...r.days].sort((a, b) => a - b);
  if (days.length === 0) return 'по числам месяца';
  if (days.length === 1) return `${days[0]}-го числа`;
  return `${days.slice(0, -1).join(', ')}-го и ${days[days.length - 1]}-го числа`;
}

// ────────────────────────────────────────────────────────────── ожидания

/** Ожидания ещё ждут денег: не получены, не отменены, не пропущены. */
export const isAwaited = (e: IncomeExpectation): boolean =>
  e.status === 'planned' || e.status === 'partial';

/** Сколько дней ожидание может висеть, прежде чем его надо разобрать. */
export const STALE_DAYS = 31;

/**
 * В каком состоянии ожидание с точки зрения человека.
 *
 * Считается, а не хранится: новое значение в поле `status` старые клиенты
 * прочли бы как «не ждём» и спрятали бы живой финансовый факт.
 *
 * · `waiting` — срок ещё не наступил;
 * · `due` — день настал, пора спросить «пришло?»;
 * · `unresolved` — висит больше месяца: горизонт оно уже не держит, но
 *   **само никуда не девается**. Приложение не вправе решить за человека,
 *   что деньги пришли, не пришли или были отменены: это финансовый факт, а
 *   не помеха интерфейсу;
 * · `closed` — вопрос закрыт: получено, пропущено или отменено.
 */
export type ExpectationState = 'waiting' | 'due' | 'unresolved' | 'closed';

export function expectationState(e: IncomeExpectation, today: PlainDate): ExpectationState {
  if (!isAwaited(e)) return 'closed';
  if (isAfter(e.expectedDate, today)) return 'waiting';
  return diffDays(e.expectedDate, today) > STALE_DAYS ? 'unresolved' : 'due';
}

/** Ожидания, которые надо разобрать: висят дольше месяца без ответа. */
export const unresolvedExpectations = (
  list: readonly IncomeExpectation[], today: PlainDate,
): IncomeExpectation[] => list
  .filter((e) => expectationState(e, today) === 'unresolved')
  .sort((a, b) => a.expectedDate.localeCompare(b.expectedDate));

/** Сколько ещё ждут по этому ожиданию. */
export const expectedRest = (e: IncomeExpectation): Money =>
  clampPos(sub(e.amount, e.receivedAmount ?? ZERO));

/**
 * Ожидания, наступления которых пора подтвердить.
 *
 * День настал — вопрос уместен. Раньше срока спрашивать не о чем, а молчать
 * после срока нельзя: приход сам деньгами не станет.
 */
export const dueExpectations = (
  list: readonly IncomeExpectation[], today: PlainDate,
): IncomeExpectation[] => list
  .filter((e) => isAwaited(e) && !isAfter(e.expectedDate, today))
  .sort((a, b) => a.expectedDate.localeCompare(b.expectedDate));

/**
 * Наступление регулярного дохода как ожидание.
 *
 * Собирается из ритма и потому имеет `dateBasis: 'schedule'`: это не обещание
 * человека, а расчёт по расписанию, которое он назвал.
 */
export function expectationFromStream(
  s: IncomeStream, today: PlainDate,
): IncomeExpectation | null {
  const date = nextForStream(s, today);
  if (!date) return null;
  return {
    id: `${s.id}:${date}`,
    name: s.name,
    amount: s.amount,
    amountBasis: amountBasisOf(s),
    expectedDate: date,
    dateBasis: 'schedule',
    accountId: s.accountId,
    streamId: s.id,
    status: 'planned',
    createdAt: '',
  };
}

// ──────────────────────────────────────────────────────── оценка по истории

/** Сколько приходов нужно, чтобы говорить об «обычном» интервале. */
export const ENOUGH_FACTS = 6;

export interface IntervalEstimate {
  /** Обычный интервал в днях. `null` — данных мало. */
  days: number | null;
  /** Сколько приходов участвовало в оценке. */
  samples: number;
  /** Оценке можно показывать дату. Иначе — только предлагать уточнить. */
  confident: boolean;
}

/**
 * Обычный интервал между приходами.
 *
 * Берётся **не медиана, а 75-й перцентиль**: если обычно раз в одиннадцать
 * дней, но бывало и двадцать, обещать надо по двадцати. Ошибка в короткую
 * сторону здесь дороже — она разрешает потратить деньги, которые придётся
 * растянуть дольше, чем думал человек.
 *
 * Возвраты, переводы и разовые деньги в историю ритма не входят: их
 * отфильтровывает вызывающий, потому что только он знает вид операции.
 */
export function estimateInterval(dates: readonly PlainDate[]): IntervalEstimate {
  const sorted = [...new Set(dates)].sort();
  const gaps: number[] = [];
  for (let i = 1; i < sorted.length; i += 1) {
    const gap = diffDays(sorted[i - 1]!, sorted[i]!);
    if (gap > 0) gaps.push(gap);
  }
  if (gaps.length === 0) return { days: null, samples: sorted.length, confident: false };

  gaps.sort((a, b) => a - b);
  // Индекс 75-го перцентиля по «ближайшему рангу»: для четырёх интервалов это
  // третий по возрастанию, то есть заметно осторожнее середины.
  const idx = Math.min(gaps.length - 1, Math.ceil(gaps.length * 0.75) - 1);
  return {
    days: gaps[idx]!,
    samples: sorted.length,
    confident: sorted.length >= ENOUGH_FACTS,
  };
}

// ──────────────────────────────────────────────────────────────── горизонт

/**
 * Насколько твёрдо известен срок.
 *
 * `awaiting` — отдельное состояние, а не разновидность «неизвестно»: деньги
 * ждали и они не пришли. Перескочить к следующей зарплате здесь нельзя —
 * человек живёт не по расписанию, а по тому, что случилось.
 */
export type HorizonBasis = 'confirmed' | 'awaiting' | 'estimated' | 'unknown';

export type HorizonSource =
  | { kind: 'stream'; id: string }
  | { kind: 'expectation'; id: string }
  | { kind: 'history' }
  /** Выплата себе из буфера стабильного бюджета. */
  | { kind: 'stable'; id: string };

export interface Horizon {
  basis: HorizonBasis;
  /** `null` только при `unknown`. */
  date: PlainDate | null;
  source: HorizonSource | null;
  /** Почему так — строкой, которую можно показать человеку. */
  why: string;
}

export interface HorizonInput {
  today: PlainDate;
  streams: readonly IncomeStream[];
  /** Ожидания, названные человеком. */
  expectations?: readonly IncomeExpectation[];
  /**
   * Приходы, которые действительно случились.
   *
   * Из них видно и то, подтверждено ли сегодняшнее наступление, и то, с какой
   * частотой приходят деньги у человека без расписания.
   */
  facts?: readonly IncomeFact[];
  /**
   * С какого дня приложение ведёт учёт.
   *
   * Раньше этой даты приходы не спрашиваются: остатки названы уже с ними.
   */
  since?: PlainDate;
  /**
   * Ближайшая выплата себе из буфера — **только обеспеченная**.
   *
   * Точкой планирования она быть вправе: деньги уже существуют и лежат в
   * резерве, ждать их не от кого. Необеспеченную сюда передавать нельзя —
   * иначе приложение обещало бы дожить до денег, которых нет, и после
   * несостоявшейся выплаты оптимистично перескочило бы на следующую дату.
   */
  selfPayout?: { id: string; date: PlainDate; name?: string } | null;
}

/**
 * Горизонт: до какого дня деньги обязаны дожить.
 *
 * Лестница, а не одна формула:
 *
 * 1. ближайшее наступление регулярного дохода по ритму — твёрдая дата;
 * 2. ожидание, дату которого человек назвал сам, — тоже твёрдая;
 * 3. оценка по истории — показывается, но твёрдой не считается, пока человек
 *    её не подтвердил;
 * 4. ничего из этого — горизонта нет, и это честный ответ.
 *
 * Смешанный случай решается сам собой: у нерегулярной подработки ритм дат не
 * называет, поэтому горизонт задаёт зарплата, а подработка только доливает
 * деньги.
 */
export function resolveHorizon(input: HorizonInput): Horizon {
  const facts = input.facts ?? [];
  const confirmedCandidates: { date: PlainDate; source: HorizonSource; why: string }[] = [];
  const awaitingCandidates: { date: PlainDate; source: HorizonSource; why: string }[] = [];

  for (const s of input.streams) {
    if (roleOfStream(s) !== 'checkpoint') continue;

    // Сегодняшний или просроченный приход — текущая точка. Пока он не
    // подтверждён, следующая не наступает: иначе утром в день зарплаты
    // горизонт прыгает на месяц вперёд, а «не пришло вчера» выглядит как
    // «придёт через месяц, живите».
    const pending = pendingOccurrence(s, input.today, facts, input.since);
    if (pending) {
      const bucket = pending === input.today ? confirmedCandidates : awaitingCandidates;
      bucket.push({
        date: pending,
        source: { kind: 'stream', id: s.id },
        why: pending === input.today
          ? `${s.name}: приход сегодня`
          : `${s.name}: ждали ${dayWords(pending, input.today)}, приход не подтверждён`,
      });
      continue;
    }

    const date = nextForStream(s, input.today);
    if (date) {
      confirmedCandidates.push({
        date,
        source: { kind: 'stream', id: s.id },
        why: `${s.name}: ${rhythmText(rhythmOf(s))}`,
      });
    }
  }

  for (const e of input.expectations ?? []) {
    if (!isAwaited(e) || e.dateBasis !== 'confirmed') continue;
    // Роль важнее даты: обещанный возврат за куртку не может быть следующей
    // точкой планирования только потому, что он завтра.
    if (roleOfExpectation(e) !== 'checkpoint') continue;
    // Зависшее ожидание горизонт не держит — но и не закрывается само:
    // оно ждёт ответа человека в «Нужно разобрать».
    if (expectationState(e, input.today) === 'unresolved') continue;

    const bucket = isBefore(e.expectedDate, input.today) ? awaitingCandidates : confirmedCandidates;
    bucket.push({
      date: e.expectedDate,
      source: { kind: 'expectation', id: e.id },
      why: isBefore(e.expectedDate, input.today)
        ? `${e.name}: ждали ${dayWords(e.expectedDate, input.today)}, приход не подтверждён`
        : `${e.name}: дату назвал ты`,
    });
  }

  /*
    Выплата себе — тоже точка, но другой природы.

    Внешнего прихода можно не дождаться, а эти деньги уже лежат в буфере:
    ждать здесь нечего, нужно лишь отдать их себе. Поэтому она попадает в
    подтверждённые кандидаты — но объяснение обязано сказать, чьи это
    деньги, иначе человек прочтёт выплату как ожидаемый заработок.
  */
  if (input.selfPayout) {
    confirmedCandidates.push({
      date: input.selfPayout.date,
      source: { kind: 'stable', id: input.selfPayout.id },
      why: 'Выплата себе из буфера — это твои деньги, а не ожидаемый внешний доход',
    });
  }

  // Неподтверждённое прошлое важнее любого будущего: сначала надо закрыть
  // вопрос «деньги пришли или нет».
  if (awaitingCandidates.length > 0) {
    const best = awaitingCandidates.reduce((a, b) => (a.date <= b.date ? a : b));
    return { basis: 'awaiting', date: best.date, source: best.source, why: best.why };
  }

  if (confirmedCandidates.length > 0) {
    const best = confirmedCandidates.reduce((a, b) => (a.date <= b.date ? a : b));
    return { basis: 'confirmed', date: best.date, source: best.source, why: best.why };
  }

  const history = facts.filter((f) => f.kind === 'regular').map((f) => f.date);
  const estimate = estimateInterval(history);
  if (estimate.confident && estimate.days !== null) {
    const last = [...history].sort().pop()!;
    const date = addDays(last, estimate.days);
    return {
      basis: 'estimated',
      // Оценка, оказавшаяся в прошлом, датой быть не может: деньги уже должны
      // были прийти и не пришли. Считаем от сегодня, но честно зовём оценкой.
      date: isAfter(input.today, date) ? addDays(input.today, estimate.days) : date,
      source: { kind: 'history' },
      why: `Обычно деньги приходят раз в ${estimate.days} дн. — оценка по истории`,
    };
  }

  return {
    basis: 'unknown',
    date: null,
    source: null,
    why: 'Дата следующего прихода неизвестна — считаю только то, что есть',
  };
}

/**
 * Твёрдая дата, на которую можно опираться при обеспечении платежей.
 *
 * Ни оценка по истории, ни неподтверждённый приход ею не являются: первая —
 * догадка, второй — вопрос без ответа.
 */
export const confirmedDate = (h: Horizon): PlainDate | null =>
  (h.basis === 'confirmed' ? h.date : null);

// ────────────────────────────────────── обещанные деньги и сегодняшние деньги

/**
 * Ожидания по возрастанию даты: ближайшее первым.
 *
 * Здесь же проходит граница, ради которой весь модуль и написан: **ожидание
 * не деньги**. Ни одна функция ниже не прибавляет ожидаемое к доступному, и
 * `computeAvailable` об ожиданиях не знает вовсе. Меняется срок, а не сумма.
 */
export const awaited = (list: readonly IncomeExpectation[]): IncomeExpectation[] =>
  list.filter(isAwaited).sort((a, b) => a.expectedDate.localeCompare(b.expectedDate));

/** Сколько ждут до названного дня включительно. */
export const awaitedBy = (
  list: readonly IncomeExpectation[], date: PlainDate,
): Money => awaited(list)
  .filter((e) => !isAfter(e.expectedDate, date))
  .reduce((sum, e) => (sum + expectedRest(e)) as Money, ZERO);

/** Что ожидаемый приход делает с нехваткой — и чего не делает. */
export interface GapOutlook {
  /** Нехватка сегодня. Ожидаемый приход её не трогает. */
  now: Money;
  /** Что останется, когда деньги придут. */
  after: Money;
  /** Когда именно. */
  on: PlainDate;
  /** Приход закрывает нехватку целиком. */
  closes: boolean;
  /** Деньги приходят позже, чем нужны: до этого дня нехватка остаётся. */
  late: boolean;
}

/**
 * Как выглядит нехватка с учётом обещанного прихода.
 *
 * `need` — день, к которому деньги нужны. Приход после него помечается
 * поздним: он нехватку не отменяет, а переносит, и звать это «закрыто» было
 * бы обманом.
 */
export function gapOutlook(
  gap: Money, e: IncomeExpectation, need: PlainDate,
): GapOutlook {
  const rest = expectedRest(e);
  const after = gap > rest ? ((gap - rest) as Money) : ZERO;
  return {
    now: gap,
    after,
    on: e.expectedDate,
    closes: after === ZERO,
    late: isAfter(e.expectedDate, need),
  };
}

/**
 * Начало текущего цикла: сначала факт, потом расписание.
 *
 * Две разные вещи называются одним словом, и путать их нельзя.
 *
 * · **Просить отложить** можно только из денег, которые пришли: там нужен
 *   факт и ничего кроме (`lastIncomeDate` в `savings.ts`).
 * · **Считать и ключевать цикл** приходится всегда — даже когда приходов ещё
 *   не записывали: иначе платежи, наступившие до сегодня, выпадут из расчёта,
 *   а пропуск взноса в цель будет некуда записать.
 *
 * Поэтому здесь факт предпочитается расписанию, но расписание остаётся
 * запасным вариантом, и это осознанная оценка, а не источник истины.
 */
export function cycleStartFrom(
  streams: readonly IncomeStream[], facts: readonly IncomeFact[], today: PlainDate,
): PlainDate | null {
  const actual = lastActualIncome(facts, today);
  if (actual) return actual;
  const dates = streams
    .map((s) => previousOccurrence(s, today))
    .filter((d): d is PlainDate => d !== null);
  return dates.length > 0 ? dates.reduce((a, b) => (a >= b ? a : b)) : null;
}
